// Layout do portal do cliente (Agente Global): funções puras sobre
// ss_blocks[].rows. ZERO imports de valor — este arquivo roda no Node
// (layout.selfcheck.ts). Tipos só via `import type`.
//
// Contrato (2026-10-26, campos desacoplados das sessões + sub-blocos):
//   Block = {key, title, description?, rows: Cell[][], groups?: {key: Group}}
//   Group = {title?, description?, rows: Cell[][]}   sub-bloco: "bolha" que junta campos num card só
//   Cell  = {session_id, width 25|50|75|100}         soma da linha ≤ 100, máx MAX_ROWS linhas
//   session_id (nome legado da coluna) = id do CAMPO (agents.ss_fields), "tool:<id>" ou
//   "group:<key>" (sub-bloco; ocupa a linha inteira, as larguras valem só pros campos de dentro).
//   Campo fora de qualquer bloco = fora do portal (bandeja na aba Ordem).

export type Width = 25 | 50 | 75 | 100;
export interface PortalMeta { title?: string; description?: string; fields?: Record<string, { label?: string; help?: string }> }
export interface Cell { session_id: string; width: Width; portal?: PortalMeta }  // portal: só célula tool:* (o que o cliente vê)
export interface Group { title?: string; description?: string; rows: Cell[][] }
export interface Block { key: string; title: string; description?: string; rows: Cell[][]; groups?: Record<string, Group> }
/** O que pode virar célula: campo editável, tool global ou item fixo de sistema (`system:*`,
 *  ex. follow-up: config do implantado que o cliente edita). `type` = rótulo (só exibição). */
export interface Item { id: string; name: string; kind: "field" | "tool" | "system"; type?: string }
/** Onde uma linha mora: no bloco ou num sub-bloco dele. */
export interface Where { blockKey: string; groupKey?: string }

export const SYSTEM_MESSAGE_ID = "system_message";
export const isToolId = (id: string) => id.startsWith("tool:");
export const isGroupId = (id: string) => id.startsWith("group:");
export const isSystemId = (id: string) => id.startsWith("system:");  // sempre 100% da linha
export const groupKeyOf = (id: string) => id.slice(6);
export const MAX_ROWS = 8;

export function uid(): string {
  try { return crypto.randomUUID(); } catch { return `s_${Date.now()}_${Math.floor(Math.random() * 1e6)}`; }
}
export const slug = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 32);

export const asWidth = (w: unknown): Width => (w === 25 || w === 50 || w === 75 || w === 100 ? w : 100);
export const rowUsed = (row: Cell[]) => row.reduce((n, c) => n + c.width, 0);
export const canPlace = (row: Cell[], width: number, ignoreId?: string) =>
  rowUsed(row.filter((c) => c.session_id !== ignoreId)) + width <= 100;

/** Linhas de um container (bloco ou sub-bloco); null se não existe. */
export function rowsAt(blocks: Block[], at: Where): Cell[][] | null {
  const b = blocks.find((x) => x.key === at.blockKey);
  if (!b) return null;
  return at.groupKey ? b.groups?.[at.groupKey]?.rows ?? null : b.rows;
}
function withRows(blocks: Block[], at: Where, rows: Cell[][]): Block[] {
  return blocks.map((b) => (b.key !== at.blockKey ? b : at.groupKey
    ? { ...b, groups: { ...b.groups, [at.groupKey]: { ...b.groups?.[at.groupKey], rows } } }
    : { ...b, rows }));
}
/** Todos os containers, na ordem: bloco e depois seus sub-blocos. */
function containers(blocks: Block[]): { at: Where; rows: Cell[][] }[] {
  return blocks.flatMap((b) => [{ at: { blockKey: b.key }, rows: b.rows },
    ...Object.entries(b.groups ?? {}).map(([k, g]) => ({ at: { blockKey: b.key, groupKey: k }, rows: g.rows }))]);
}

/** Container (bloco ou sub-bloco) onde a célula está. */
export function whereIs(blocks: Block[], id: string): Where | null {
  return containers(blocks).find((c) => c.rows.some((r) => r.some((x) => x.session_id === id)))?.at ?? null;
}
export function blockOf(blocks: Block[], id: string): string | null {
  return whereIs(blocks, id)?.blockKey ?? null;
}
export function cellOf(blocks: Block[], id: string): Cell | null {
  for (const c of containers(blocks)) for (const r of c.rows) for (const x of r) if (x.session_id === id) return x;
  return null;
}

/** Itens (campos e tools) que não estão em nenhum bloco nem sub-bloco. */
export function unplaced(items: Item[], blocks: Block[]): Item[] {
  return items.filter((it) => !blockOf(blocks, it.id));
}

/** Estado vindo do servidor → estado válido do builder (idempotente): célula morta/duplicada
 *  some, width inválido vira 100, container sem linha ganha uma vazia. Célula tool:* fica sempre
 *  (a lista de tools chega depois do init). Célula group:* sem grupo some; grupo sem célula some. */
export function normalizeLayout(items: Item[], blocks: Block[]): Block[] {
  const known = new Set(items.map((it) => it.id));
  const seen = new Set<string>();
  const clean = (rows: Cell[][] | undefined, groups: Record<string, Group>, nested: boolean, bkey: string): Cell[][] => {
    const out = (rows ?? []).map((row) => {
      const cells: Cell[] = [];
      for (const c of row ?? []) {
        const id = c.session_id;
        if (!isGroupId(id) && seen.has(id)) continue;
        if (isGroupId(id)) {
          // sub-bloco só no 1º nível e só se existe; chave é por bloco (dois blocos podem ter "sub_1")
          if (nested || !Object.hasOwn(groups, groupKeyOf(id)) || seen.has(`${bkey}/${id}`)) continue;
          seen.add(`${bkey}/${id}`);
          cells.push({ session_id: id, width: 100 });
          continue;
        }
        // tool:* e system:* ficam sempre (a lista de tools chega depois do init; itens de sistema são fixos)
        if (!known.has(id) && !isToolId(id) && !isSystemId(id)) continue;
        seen.add(id);
        cells.push({ session_id: id, width: isSystemId(id) ? 100 : asWidth(c.width), ...(c.portal ? { portal: c.portal } : {}) });
      }
      return cells;
    });
    if (out.length === 0) out.push([]);
    return out;
  };
  return blocks.filter((b) => b.key).map((b) => {
    const groups = b.groups ?? {};
    const rows = clean(b.rows, groups, false, b.key);
    const used = new Set(rows.flat().filter((c) => isGroupId(c.session_id)).map((c) => groupKeyOf(c.session_id)));
    const g: Record<string, Group> = {};
    for (const k of Object.keys(groups)) if (used.has(k)) g[k] = { title: groups[k].title ?? "", description: groups[k].description ?? "", rows: clean(groups[k].rows, groups, true, b.key) };
    return { key: b.key, title: b.title ?? "", description: b.description ?? "", rows, ...(Object.keys(g).length ? { groups: g } : {}) };
  });
}

/** JSON com chaves ordenadas: comparar estado do builder com o do servidor sem falso "sujo". */
export const stable = (v: unknown): string => JSON.stringify(v, (_k, x) => (x && typeof x === "object" && !Array.isArray(x) ? Object.fromEntries(Object.keys(x).sort().map((k) => [k, x[k]])) : x));

/** Linhas vazias não vão pro servidor. */
export const stripEmptyRows = (blocks: Block[]): Block[] => blocks.map((b) => ({
  ...b, rows: b.rows.filter((r) => r.length > 0),
  ...(b.groups ? { groups: Object.fromEntries(Object.entries(b.groups).map(([k, g]) => [k, { ...g, rows: g.rows.filter((r) => r.length > 0) }])) } : {}),
}));

export function removeCell(blocks: Block[], id: string): Block[] {
  const strip = (rows: Cell[][]) => rows.map((r) => r.filter((c) => c.session_id !== id));
  return blocks.map((b) => ({ ...b, rows: strip(b.rows),
    ...(b.groups ? { groups: Object.fromEntries(Object.entries(b.groups).map(([k, g]) => [k, { ...g, rows: strip(g.rows) }])) } : {}) }));
}

/** Move (ou insere) a célula numa linha de um container. `rowIdx: "new"` cria linha (até MAX_ROWS).
 *  `beforeId` insere antes daquela célula. Width é mantido ou reduzido ao que sobra; sem espaço →
 *  null (recusa). Sub-bloco (group:*) só vai pro 1º nível do bloco, sempre com 100%. */
export function moveCell(
  blocks: Block[], id: string, to: Where & { rowIdx: number | "new"; beforeId?: string },
): Block[] | null {
  const group = isGroupId(id);
  if (group && to.groupKey) return null;
  if (group && whereIs(blocks, id)?.blockKey !== to.blockKey) return null;  // grupo pertence ao bloco onde nasceu
  const cur = cellOf(blocks, id);
  const full = group || isSystemId(id);
  const width: Width = full ? 100 : cur?.width ?? 100;
  const keep = cur?.portal ? { portal: cur.portal } : {};  // tool: título/rótulos do cliente sobrevivem ao arrasto
  const next = removeCell(blocks, id);
  const rows = rowsAt(next, to);
  if (!rows) return null;
  if (to.rowIdx === "new") {
    const live = rows.filter((r) => r.length > 0);  // linha que a célula acabou de deixar não conta
    if (live.length >= MAX_ROWS) return null;
    return withRows(next, to, [...live, [{ session_id: id, width, ...keep }]]);
  }
  const row = rows[to.rowIdx];
  if (!row) return null;
  const free = 100 - rowUsed(row);
  if (free < 25 || (full && free < 100)) return null;
  const cell: Cell = { session_id: id, width: asWidth(Math.min(width, free)), ...keep };
  const at = to.beforeId ? row.findIndex((c) => c.session_id === to.beforeId) : -1;
  const newRow = [...row];
  newRow.splice(at < 0 ? row.length : at, 0, cell);
  return withRows(next, to, rows.map((r, i) => (i === to.rowIdx ? newRow : r)));
}

export function setWidth(blocks: Block[], id: string, width: Width): Block[] | null {
  if (isGroupId(id) || isSystemId(id)) return null;
  const at = whereIs(blocks, id);
  const rows = at && rowsAt(blocks, at);
  const row = rows?.find((r) => r.some((c) => c.session_id === id));
  if (!at || !rows || !row || !canPlace(row, width, id)) return null;
  return withRows(blocks, at, rows.map((r) => (r !== row ? r : r.map((c) => (c.session_id === id ? { ...c, width } : c)))));
}

export function moveBlock(blocks: Block[], key: string, dir: -1 | 1): Block[] {
  const i = blocks.findIndex((b) => b.key === key), j = i + dir;
  if (i < 0 || j < 0 || j >= blocks.length) return blocks;
  const next = [...blocks]; [next[i], next[j]] = [next[j], next[i]]; return next;
}

export function setCellPortal(blocks: Block[], id: string, portal: PortalMeta): Block[] {
  const at = whereIs(blocks, id);
  const rows = at && rowsAt(blocks, at);
  if (!at || !rows) return blocks;
  return withRows(blocks, at, rows.map((r) => r.map((c) => (c.session_id === id ? { ...c, portal } : c))));
}

export function addRow(blocks: Block[], at: Where): Block[] {
  const rows = rowsAt(blocks, at);
  if (!rows || rows.length >= MAX_ROWS) return blocks;
  return withRows(blocks, at, [...rows, []]);
}

/** Cria um sub-bloco numa linha nova do bloco (até MAX_ROWS). */
export function addGroup(blocks: Block[], blockKey: string): Block[] | null {
  const b = blocks.find((x) => x.key === blockKey);
  if (!b) return null;
  const live = b.rows.filter((r) => r.length > 0);
  if (live.length >= MAX_ROWS) return null;
  // chave única no agente inteiro: whereIs/cellOf/moveCell acham a célula pelo id "group:<key>"
  const key = `sub_${uid().replace(/-/g, "").slice(0, 8)}`;
  return blocks.map((x) => (x.key !== blockKey ? x : {
    ...x, rows: [...live, [{ session_id: `group:${key}`, width: 100 }]],
    groups: { ...x.groups, [key]: { title: "", description: "", rows: [[]] } },
  }));
}

/** Apaga o sub-bloco; os campos de dentro voltam pra bandeja. */
export function removeGroup(blocks: Block[], blockKey: string, groupKey: string): Block[] {
  return blocks.map((b) => {
    if (b.key !== blockKey) return b;
    const { [groupKey]: _gone, ...rest } = b.groups ?? {};
    return { ...b, rows: b.rows.map((r) => r.filter((c) => c.session_id !== `group:${groupKey}`)), ...(Object.keys(rest).length ? { groups: rest } : { groups: undefined }) };
  }).map((b) => (b.groups === undefined ? (({ groups: _g, ...r }) => r)(b) : b));
}

export function setGroup(blocks: Block[], blockKey: string, groupKey: string, patch: Partial<Group>): Block[] {
  return blocks.map((b) => (b.key !== blockKey || !b.groups?.[groupKey] ? b : { ...b, groups: { ...b.groups, [groupKey]: { ...b.groups[groupKey], ...patch } } }));
}

/** base, base_2, base_3… fora de `taken`. */
export function uniqueKey(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}_${n}`)) n++;
  return `${base}_${n}`;
}

/** Chave de bloco é bloco_N para sempre (portal exibe title || key). */
export function newBlockKey(blocks: Block[]): string {
  let n = blocks.length + 1;
  while (blocks.some((b) => b.key === `bloco_${n}`)) n++;
  return `bloco_${n}`;
}
